import {
  from,
  ApolloClient,
  HttpLink,
  InMemoryCache,
  ApolloLink,
  split,
  concat,
} from '@apollo/client'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { config } from '_appConfig'
import { createClient } from 'graphql-ws'

import { cleanTypenameLink } from 'utils/apollo-clear-typename'

import { errorLink } from './__utils__/links'
import {
  employeesAllMerge,
  fileMetaMerge,
  dashboardCommentsMerge,
  dashboardProjectsMerge,
  editorBlocksPatchesMerge,
  templatesAllMerge,
  unsplashFilesMerge,
  employeePositionsMerge,
  blocksDeletedAllMerge,
} from './__utils__/merge'

// https://www.apollographql.com/docs/react/networking/network-layer/#apollo-link
// Session-based auth - cookies are sent automatically with credentials: 'include'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const middleware = new ApolloLink((operation: any, forward: any) => {
  // No need to send visitorToken anymore - visitor sessions are handled server-side
  return forward(operation)
})

const afterwareLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const context = operation.getContext()
    const headers = context.response?.headers
    if (headers) {
      // get version from headers through get field response
      const currentVersion = headers.get('Version-Backend-Build')
      if (currentVersion) {
        localStorage.setItem('current_version', currentVersion)
      }
    }

    return response
  })
})

const coreLink = new HttpLink({
  uri: config.io.coreEndpoint,
  credentials: 'include', // 🔥 Important: include cookies for session auth
})

const authLink = new HttpLink({
  uri: config.io.authEndpoint,
  credentials: 'include', // 🔥 Important: include cookies for session auth
})

const serverStorageLink = new HttpLink({})

export const getCoreWsLink = (sessionIdOrToken?: string) => {
  const wsLink = new GraphQLWsLink(
    createClient({
      url: config.io.coreEndpointWs || '',
      lazy: true, // Don't connect until first subscription
      shouldRetry: () => {
        // notify({ type: NotificationType.info, message: '', duration: 2000 })
        return true
      },
      connectionParams: () => {
        // If we have a sessionId/token from getToken query, use it
        if (sessionIdOrToken) {
          return {
            sessionId: sessionIdOrToken,
            headers: {
              Authorization: `Bearer ${sessionIdOrToken}`, // Backward compatibility
            },
          }
        }

        // Return empty params if no session
        return {}
      },
      // on: {
      //   connecting: () =>
      //     notify({
      //       type: NotificationType.info,
      //       message: 'WebSocket подключается...',
      //       duration: 2000,
      //     }),
      //   connected: () =>
      //     notify({
      //       type: NotificationType.success,
      //       message: 'WebSocket подключен',
      //       duration: 2000,
      //     }),
      //   closed: () =>
      //     notify({
      //       type: NotificationType.error,
      //       message: 'WebSocket закрыт',
      //       duration: 2000,
      //     }),
      //   error: (error) =>
      //     notify({
      //       type: NotificationType.error,
      //       message: `Ошибка WebSocket: ${error}`,
      //       duration: 2000,
      //     }),
      // },
    }),
  )

  const splittedLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query)
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
    },
    wsLink,
    coreLink,
  )

  return from([cleanTypenameLink, afterwareLink, errorLink, concat(middleware, splittedLink)])
}

const gqlClient = {
  core: new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        EditorSectionUpdate: {
          keyFields: ['section', ['id']],
        },
        Block: {
          keyFields: ['uuid'],
        },
        EditorTemplate: {
          keyFields: ['id'],
        },
        ProjectTranslateAuto: {
          keyFields: ['translateId'],
        },
        // TODO have id field for EditorSectionTest
        // EditorSectionTest: {
        //   keyFields: [
        //     'time',
        //     'testRetryCount',
        //     'testLimit',
        //     'testThreshold',
        //     'isNextOnSubmit',
        //     'isValidationVisible',
        //     'isResultValuePercent',
        //     'questionOrder',
        //     'isShuffleQuestions',
        //     'isProgressShown',
        //     'isUnableToExit',
        //   ],
        // },
        Link: {
          keyFields: ['shortLink'],
        },
        BlockVersionItem: {
          keyFields: ['uuid', 'version'],
        },
        UsageBlockItem: {
          keyFields: ['fileUsageId'],
        },
        Query: {
          fields: {
            fileMetaGroupAll: fileMetaMerge,
            employeesAll: employeesAllMerge,
            dashboardComments: dashboardCommentsMerge,
            dashboardProjects: dashboardProjectsMerge,
            editorBlocksPatches: editorBlocksPatchesMerge,
            editorTemplatesAll: templatesAllMerge,
            unsplashExternalImages: unsplashFilesMerge,
            getEmployeePositions: employeePositionsMerge,
            editorBlocksArchived: blocksDeletedAllMerge,
          },
        },
      },
      possibleTypes: {
        CompanyInfoUnion: ['Company', 'CompanyWithoutAccess'],
      },
    }),

    link: getCoreWsLink(),
  }),
  auth: new ApolloClient({
    cache: new InMemoryCache({
      possibleTypes: {
        Error: ['InputError', 'ValidationError', 'DependencyError', 'CommonError', 'AttemptsError'],
      },
    }),
    link: from([cleanTypenameLink, errorLink, concat(middleware, authLink)]),
  }),
  serverStorage: new ApolloClient({
    cache: new InMemoryCache(),
    link: from([cleanTypenameLink, errorLink, concat(middleware, serverStorageLink)]),
  }),
}

export { gqlClient }
